import { useEffect, useEffectEvent, useState } from "react";

import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useSearchParams } from "react-router";

import { useSessionStore } from "stores/session";

import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H1 } from "simplydo/react/ui/header";
import { Loader } from "simplydo/react/ui/loader";
import { Message } from "simplydo/react/ui/message";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

const LoginWithToken = () => {
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const user = useCurrentUser();

  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const next = searchParams.get("next");

  let navigateTo = "/";
  let decodedNext: string | null = null;
  if (next) {
    decodedNext = decodeURIComponent(next);
    navigateTo = decodedNext;
  }

  const navigate = useNavigate();

  // The token is single use, so sign in once per token, not again when the
  // signed-in user loads.
  const signIn = useEffectEvent((token: string) => {
    if (decodedNext) {
      const nextSplit = decodedNext.split("?");
      if (nextSplit.length > 1) {
        const nextParams = new URLSearchParams(nextSplit[1]);
        const utmLinkReferrer = nextParams.get("utm-link");
        if (utmLinkReferrer) {
          useSessionStore.setState({ utmLink: utmLinkReferrer });
        }
      }
    }

    api.auth.loginWithToken(
      token,
      () => {
        setLoading(false);
        if (user) {
          api.invalidateQueries(api.users.me);
        }
        navigate(navigateTo);
      },
      (err) => {
        setLoading(false);
        setErrorMessage(err.message);
      },
    );
  });

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    signIn(token);
  }, [token]);

  if (!token) {
    return <Navigate to={user ? navigateTo : "/"} replace={true} />;
  }

  return (
    <div>
      <H1>{t("entry.login.justAMoment")}</H1>
      <Divider />
      {loading ? (
        <div>
          <p>{t("entry.login.token.wait")}</p>
          <Loader />
          <Divider hidden />
        </div>
      ) : null}
      {errorMessage ? (
        <div>
          <Message color="orange">
            {t("entry.login.token.failed")} {errorMessage}
          </Message>
          <ButtonLink color="primary" to="/">
            Return to {user ? "homepage" : "login"}
          </ButtonLink>
        </div>
      ) : null}
    </div>
  );
};

export default LoginWithToken;
